// Slack messaging (BEAST-9422) - pure helpers shared by the Slack slice, sagas and UI.
import { format, isValid, parseISO } from 'date-fns';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'utils/dateTimeUtils';

export const SLACK_SEND_STATUS = Object.freeze({
    SENDING: 'SENDING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    UNKNOWN: 'UNKNOWN',
    // 400 from the backend: not sent (already sent, in progress, too long, ...); nothing to retry.
    REJECTED: 'REJECTED',
});

// Results after which reopening the dialog offers Retry (the same request again) instead of Send.
const RETRY_STATUSES = new Set([SLACK_SEND_STATUS.FAILED, SLACK_SEND_STATUS.UNKNOWN]);

export const isSlackRetryStatus = (status) => RETRY_STATUSES.has(status);

// Final statuses pushed over the desk websocket (.SlackSendResultMessage) once the backend has finished the delivery.
const RESULT_STATUSES = new Set([SLACK_SEND_STATUS.SUCCESS, SLACK_SEND_STATUS.FAILED, SLACK_SEND_STATUS.UNKNOWN]);

export const toSlackResultStatus = (status) => (RESULT_STATUSES.has(status) ? status : SLACK_SEND_STATUS.UNKNOWN);

// Timing of the async send, tied to the backend: the POST only records the send and answers at once with
// { id, status: PENDING }; the backend delivers it (up to 3 attempts) and closes a stuck send as UNKNOWN after 2 minutes,
// after which a new POST is accepted again. Change these together.
// - REQUEST: axiosAiq has no default timeout; a POST that never answers would keep Slack disabled until a page reload.
// - RESULT: a bit longer than the backend's 2 minutes, so a missed websocket message still ends as UNKNOWN and a retry
//   is accepted (a shorter wait would make the retry fail as "in progress").
// - LATE_RESULT: how long a send the FE gave up on still waits for its real result (see the late-result saga).
export const SLACK_SEND_REQUEST_TIMEOUT_MS = 15000;
export const SLACK_SEND_RESULT_TIMEOUT_MS = 130000;
export const SLACK_LATE_RESULT_TTL_MS = 5 * 60 * 1000;

export const SLACK_SEND_RESULT_MESSAGE_TYPE = '.SlackSendResultMessage';

// .SlackSendResultMessage (snake_case) -> the payload of slackActions.sendResultReceived.
export const parseSlackSendResultMessage = (data) => ({
    transactionId: data?.transaction_id ?? null,
    logId: data?.log_id ?? null,
    status: data?.status ?? null,
    detail: data?.detail ?? null,
});

// The POST answers with { id }, the websocket result carries the same value as log_id; compared as strings to be safe.
export const isSameSlackLogId = (a, b) => a != null && b != null && String(a) === String(b);

export const SLACK_MESSAGES_STATUS = Object.freeze({
    IDLE: 'IDLE',
    LOADING: 'LOADING',
    LOADED: 'LOADED',
    ERROR: 'ERROR',
});

export const SLACK_MAX_TEXT_LENGTH = 39000;

export const SLACK_COPY = Object.freeze({
    button: 'Slack',
    buttonTooltip: 'Send a Slack message related to the current transaction',
    dialogTitle: 'Send to Slack',
    messageTypeLabel: 'Message type',
    messageTypePlaceholder: 'Select message type',
    noMessageTypes: 'No message types',
    channelDestination: 'Channel destination:',
    previewLabel: 'Message preview',
    freeTextPlaceholder: 'Type your message',
    helperGenerated: 'Generated from transaction data. Make sure all fields are filled in.',
    helperLocked: 'Generated from transaction data. This message type cannot be edited, update the transaction fields instead.',
    helperFreeText: 'Type the message to send.',
    copy: 'Copy message',
    copied: 'Copied',
    cancel: 'Cancel',
    send: 'Send to Slack',
    sendingTitle: 'Sending to Slack...',
    successTitle: 'Successfully sent to Slack',
    unknownTitle: 'Slack status unknown',
    failedTitle: "Couldn't send to Slack",
    retry: 'Retry',
    rejectedFallback: 'The message was not sent.',
    loadingMessages: 'Loading Slack messages...',
    noMessagesTitle: 'No Slack messages available',
    noMessagesDescription: "There are no Slack messages available for this transaction's lane. Contact your administrator if you expect one here.",
    loadFailedTitle: "Couldn't load Slack messages",
    loadFailedDescription: 'Something went wrong while loading the Slack message types for this transaction.',
    close: 'Close',
    tryAgain: 'Try again',
});

// Slack is on only when the transaction's location has slackEnabled === true (null / missing counts as off).
// Takes several transaction shapes (form transactionData, panel transaction) and uses the first that carries a location.
export const resolveSlackEnabled = (...transactions) => {
    const location = transactions.map((item) => item?.gate?.location).find(Boolean);
    return location?.slackEnabled === true;
};

export const formatSlackChannel = (channelName) => {
    if (!channelName) return '';
    return channelName.startsWith('#') ? channelName : `#${channelName}`;
};

export const buildSuccessDescription = (subject, channelName) => `${subject} sent to ${formatSlackChannel(channelName)}`;

export const buildSendingDescription = (subject, channelName) => `${subject} → ${formatSlackChannel(channelName)}`;

// No technical detail here: an UNKNOWN detail is a backend exception (e.g. a read timeout), not something for the Agent.
export const buildUnknownDescription = (subject, channelName) =>
    `${subject} may have been delivered to ${formatSlackChannel(channelName)}. Check the channel before you retry.`;

export const buildFailedDescription = (subject, channelName, detail) => {
    const description = `${subject} was not sent. You can send it manually to ${formatSlackChannel(channelName)}`;
    return detail ? `${description}\nError: ${detail}` : description;
};

// One key per send, not per transaction: a closed snackbar stays on screen for its exit animation, and a retry started
// meanwhile must not share its key (closing by key would then hit both, or the wrong one).
export const slackSendingSnackbarKey = (transactionId, sendNumber) => `slack-sending-${transactionId}-${sendNumber}`;

// Light "Sending to Slack..." snackbar with a spinner; stays until the saga closes it by its key.
export const buildSlackSendingSnackbar = (transactionId, { subject, channelName, sendNumber } = {}) => ({
    title: SLACK_COPY.sendingTitle,
    message: buildSendingDescription(subject, channelName),
    loading: true,
    options: { key: slackSendingSnackbarKey(transactionId, sendNumber), persist: true },
});

// Snackbar payload for snackbarActions.enqueueSnackbar (rendered by Notifier with a bold title). Result snackbars hide
// after the app's default time like the other snackbars, and close with an X icon (design) instead of "Dismiss".
const resultSnackbar = (title, message, variant) => ({ title, message, closeIcon: true, options: { variant } });

export const buildSlackResultSnackbar = (status, { subject, channelName, detail } = {}) => {
    if (status === SLACK_SEND_STATUS.SUCCESS) {
        return resultSnackbar(SLACK_COPY.successTitle, buildSuccessDescription(subject, channelName), 'success');
    }
    if (status === SLACK_SEND_STATUS.UNKNOWN) {
        return resultSnackbar(SLACK_COPY.unknownTitle, buildUnknownDescription(subject, channelName), 'warning');
    }
    if (status === SLACK_SEND_STATUS.REJECTED) {
        // The backend's own message (already sent, in progress, too long, ...).
        return resultSnackbar(SLACK_COPY.failedTitle, detail || SLACK_COPY.rejectedFallback, 'error');
    }
    return resultSnackbar(SLACK_COPY.failedTitle, buildFailedDescription(subject, channelName, detail), 'error');
};

export const isSlackTextTooLong = (text) => (text || '').length > SLACK_MAX_TEXT_LENGTH;

export const isSlackTextSendable = (text) => !!text && !!text.trim() && !isSlackTextTooLong(text);

export const isFreeTextMessage = (message) => !!message?.freeText && !(message?.body || '').trim();

// Message to show the Agent, first match wins:
// 1. a plain-text body (e.g. the 429 rate-limit response);
// 2. `message` (Spring's default error body);
// 3. details[0].message - the app's body is { timestamp, status, path, error, details }, and for a request validation
//    error `error` is generic while the specific reason is in details[0].message;
// 4. `error`;
// 5. the axios error message (no response body).
export const getSlackErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.message || data?.details?.[0]?.message || data?.error || err?.message || '';
};

// 400 from the send endpoint: the backend rejected the message, nothing was posted (ALREADY_SENT, SEND_IN_PROGRESS, ...).
export const isSlackRejectedError = (err) => err?.response?.status === 400;

// No response at all (timeout, connection dropped): the request may still have reached Slack.
export const isSlackOutcomeUnknownError = (err) => !!err && !err.response;

// Template placeholders: {{dataField}}, e.g. {{person.fullName}} or {{gate.name}}.
const PLACEHOLDER_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;

// '/' is the form's "no value" sentinel.
const isEmptyValue = (value) => value === undefined || value === null || value === '' || value === '/';

const readPath = (obj, path) => {
    if (!obj || !path) return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];
    return path.split('.').reduce((current, key) => (current == null ? undefined : current[key]), obj);
};

// Ids are compared as strings: a lookup id can arrive as 7 in one place and '7' in another.
const sameLookupValue = (a, b) => a != null && b != null && String(a) === String(b);

const lookupDisplay = (value, lookup) => {
    if (!lookup?.dataSource || !lookup?.displayExpr) return value;
    // Multi-select lookups hold an array of ids.
    if (Array.isArray(value)) return value.map((item) => lookupDisplay(item, lookup));
    const valueExpr = lookup.valueExpr ?? lookup.displayExpr;
    const match = lookup.dataSource.find((option) => sameLookupValue(option?.[valueExpr], value));
    return match ? match[lookup.displayExpr] : value;
};

// Date fields hold the backend string as-is. parseISO reads a date-only value ('2026-10-09') as a local date, where
// new Date() reads it as UTC midnight and shows the previous day west of UTC; non-ISO values fall back to new Date().
const toDate = (value) => {
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
        const parsed = parseISO(value);
        if (isValid(parsed)) return parsed;
    }
    return new Date(value);
};

// Same formats as the rest of the app (utils/dateTimeUtils), in the Agent's local time.
const formatDateValue = (value, dataType) => {
    const date = toDate(value);
    if (!isValid(date)) return value;
    return format(date, dataType === 'date' ? DATE_FORMAT : DATE_TIME_FORMAT);
};

const isDateField = (field) => field?.dataType === 'date' || field?.dataType === 'datetime';

// Transaction properties have no field config to say they are dates, so an ISO timestamp is recognised by its shape.
const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const toText = (value) => {
    if (isEmptyValue(value)) return '';
    if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return formatDateValue(value, 'datetime');
    if (typeof value === 'string' && ISO_DATE_TIME_REGEX.test(value)) return String(formatDateValue(value, 'datetime'));
    if (typeof value === 'object') return toText(value.name ?? value.label ?? value.value ?? '');
    return String(value);
};

const formatFieldValue = (value, field) => {
    const display = lookupDisplay(value, field?.lookup);
    return isDateField(field) && !isEmptyValue(display) ? formatDateValue(display, field.dataType) : display;
};

// The raw value a placeholder resolves from, before it is turned into text (see resolveSlackPlaceholder).
const readPlaceholderValue = (key, { values, automation, fields, transactionData } = {}) => {
    const field = fields?.find((item) => item?.dataField === key);
    const formValue = values?.[key];
    if (!isEmptyValue(formValue)) return formatFieldValue(formValue, field);

    const suggestion = automation?.[key]?.sourceValue;
    if (!isEmptyValue(suggestion)) return formatFieldValue(suggestion, field);

    return readPath(transactionData, key);
};

/**
 * Resolves one placeholder against the latest transaction data, in order:
 * the form value (lookup ids shown by their display text, date fields formatted), the automation suggestion shown in
 * an empty field, then the transaction object itself (e.g. gate.name). Missing values resolve to ''.
 */
export const resolveSlackPlaceholder = (key, context) => toText(readPlaceholderValue(key, context));

export const resolveSlackTemplate = (body, context) => (body || '').replace(PLACEHOLDER_REGEX, (_, key) => resolveSlackPlaceholder(key, context));

/**
 * Placeholders that have a value but still resolve to '' - an object with no name / label / value to print (e.g.
 * {{gate.location}} instead of {{gate.location.name}}). Like the unknown ones, they usually mean the template points at
 * the wrong dataField.
 */
export const findUnprintableSlackPlaceholders = (body, context = {}) => {
    const keys = [...(body || '').matchAll(PLACEHOLDER_REGEX)].map((match) => match[1]);
    const unprintable = keys.filter((key) => {
        const value = readPlaceholderValue(key, context);
        const empty = isEmptyValue(value) || (Array.isArray(value) && value.length === 0);
        return !empty && toText(value) === '';
    });
    return [...new Set(unprintable)];
};

/**
 * Placeholders that cannot resolve to anything: no form field, no form value and no transaction property with that
 * name. They silently become '' in the text, so they usually mean a mistyped dataField in the template configuration
 * (e.g. {{trailer.number}} instead of {{trailer.trailerNumber}}). An empty value of a known field is not reported.
 */
export const findUnknownSlackPlaceholders = (body, { values, fields, transactionData } = {}) => {
    const keys = [...(body || '').matchAll(PLACEHOLDER_REGEX)].map((match) => match[1]);
    const unknown = keys.filter(
        (key) =>
            !fields?.some((item) => item?.dataField === key) &&
            !(values && Object.prototype.hasOwnProperty.call(values, key)) &&
            readPath(transactionData, key) === undefined
    );
    return [...new Set(unknown)];
};
